/**
 * Start Workflow Dialog
 * 
 * Dialog to start a new workflow instance on an entity (Part, Opportunity, etc.)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Loader2, Workflow, ChevronRight, CheckCircle2 } from 'lucide-react';
import * as workflowApi from '../../api/workflows';

// Utility for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

// Color classes for templates
const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  green: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  red: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
};

function TemplateCard({ template, selected, onSelect }) {
  const colors = COLOR_CLASSES[template.color] || COLOR_CLASSES.blue;
  const stepsCount = template.steps?.length || 0;

  return (
    <div
      onClick={onSelect}
      className={cn(
        "p-4 rounded-lg border-2 cursor-pointer transition-all",
        selected 
          ? "border-blue-500 bg-blue-50" 
          : "border-gray-200 hover:border-gray-300 bg-white"
      )}
      data-testid={`template-option-${template.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn("p-2 rounded-lg", colors.bg)}>
          <Workflow className={cn("w-5 h-5", colors.text)} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900">{template.name}</h4>
          {template.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {template.description}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="secondary" className="text-xs">
              {stepsCount} {stepsCount === 1 ? 'step' : 'steps'}
            </Badge>
          </div>
        </div>
        {selected && (
          <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

export default function StartWorkflowDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityName,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [workflowName, setWorkflowName] = useState('');

  // Fetch available templates for this entity type
  const { data: templatesData, isLoading: loadingTemplates } = useQuery({
    queryKey: ['workflow-templates', entityType],
    queryFn: () => workflowApi.listTemplates({ entityType }),
    enabled: open,
  });

  const templates = templatesData?.data || [];

  // Start workflow mutation
  const startMutation = useMutation({
    mutationFn: (data) => workflowApi.startWorkflow(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['workflow-instances']);
      queryClient.invalidateQueries(['entity-workflows', entityType, entityId]);
      toast.success('Workflow started successfully');
      onOpenChange(false);
      if (onSuccess) {
        onSuccess(response.data);
      }
      // Reset form
      setSelectedTemplateId(null);
      setWorkflowName('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to start workflow');
    },
  });

  const handleStart = () => {
    if (!selectedTemplateId) {
      toast.error('Please select a workflow template');
      return;
    }

    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    const defaultName = `${selectedTemplate?.name || 'Workflow'} - ${entityName || entityId}`;

    startMutation.mutate({
      template_id: selectedTemplateId,
      entity_type: entityType,
      entity_id: entityId,
      name: workflowName || defaultName,
      context_data: {
        entity_name: entityName,
      },
    });
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="start-workflow-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5 text-blue-600" />
            Start Workflow
          </DialogTitle>
          <DialogDescription>
            Select a workflow template to start for this {entityType}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Entity Info */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Starting workflow for: <span className="font-medium text-gray-900">{entityName || entityId}</span>
            </p>
          </div>

          {/* Template Selection */}
          <div className="space-y-3">
            <Label>Select Workflow Template</Label>
            
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Workflow className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p>No workflow templates available for {entityType}s</p>
                <p className="text-sm mt-1">Contact your admin to create workflow templates</p>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    selected={selectedTemplateId === template.id}
                    onSelect={() => setSelectedTemplateId(template.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Workflow Name */}
          {selectedTemplateId && (
            <div className="space-y-2">
              <Label htmlFor="workflow-name">Workflow Name (optional)</Label>
              <Input
                id="workflow-name"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder={`${selectedTemplate?.name || 'Workflow'} - ${entityName || entityId}`}
                data-testid="workflow-name-input"
              />
              <p className="text-xs text-gray-500">
                Leave blank to use the default name
              </p>
            </div>
          )}

          {/* Preview Steps */}
          {selectedTemplate && selectedTemplate.steps?.length > 0 && (
            <div className="space-y-2">
              <Label>Workflow Steps</Label>
              <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
                {selectedTemplate.steps.slice(0, 5).map((step, index) => (
                  <React.Fragment key={step.id}>
                    <Badge variant="outline" className="text-xs">
                      {step.name}
                    </Badge>
                    {index < Math.min(selectedTemplate.steps.length - 1, 4) && (
                      <ChevronRight className="w-3 h-3 text-gray-400" />
                    )}
                  </React.Fragment>
                ))}
                {selectedTemplate.steps.length > 5 && (
                  <Badge variant="outline" className="text-xs">
                    +{selectedTemplate.steps.length - 5} more
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={startMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStart}
            disabled={!selectedTemplateId || startMutation.isPending}
            data-testid="start-workflow-submit"
          >
            {startMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Workflow className="w-4 h-4 mr-2" />
                Start Workflow
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
